import { skipToken } from "@reduxjs/toolkit/query";
import {
  PERMISSION_TARGET_SEARCH_MAXIMUM_LENGTH,
  canSearchTargetsOf,
  useSearchPermissionTargetsQuery,
} from "permissions/_api/permission-targets-api-service";
import { NAME_SEARCH_LIMIT } from "permissions/_views/people/person-record-grants-model";
import useReadableRecordNameSearch, { READABLE_SEARCH_RECORD_TYPES } from "permissions/_views/people/use-readable-record-name-search";
import { useMemo } from "react";

export type RecordNameSearch = Readonly<{
  /** The ids of the records whose names match, or undefined while there is no search or no answer. */
  ids?: readonly string[];
  isSearching: boolean;
  error?: string;
}>;

/**
 * Which records a name search can find.
 *
 * - `organization`: every record of the organization, through each center's permission-targets
 *   search (#6174). Those searches are gated on Member Permissions, so only a viewer who holds them
 *   may ask.
 * - `readable`: only the records the viewer may open, through each record's own domain search. This
 *   is what a member without Member Permissions has on their own permissions page.
 */
export type RecordSearchScope = "organization" | "readable";

/** The reason a failed search gives. The center's own reason is not passed on: it answers many kinds at once. */
export const RECORD_SEARCH_FAILED = "Try again in a moment.";

/** The reason a search for more text than a center accepts gives, rather than waiting for no answer. */
export const RECORD_SEARCH_TOO_LONG = `Search for ${PERMISSION_TARGET_SEARCH_MAXIMUM_LENGTH} characters or fewer.`;

/** Whether records of the type can be found by name in the scope. */
export function canSearchRecordsOf(resourceType: string, scope: RecordSearchScope): boolean {
  return scope === "organization" ? canSearchTargetsOf(resourceType) : READABLE_SEARCH_RECORD_TYPES.has(resourceType);
}

/**
 * Finds records of one per-record kind by name. The Permissions API has no name search, and names
 * live in the business centers. The person page then asks for the person's grants on just those ids,
 * with the generic `targetIds` filter.
 *
 * In the `organization` scope, the targets search answers every record of the organization whose
 * name matches, whether or not the administrator may open it, so a grant on a record they cannot read
 * is found by name as well (#6795). In the `readable` scope, see `useReadableRecordNameSearch` for
 * what it cannot find. Either answers the newest {@link NAME_SEARCH_LIMIT} matches; a narrower search
 * finds the rest.
 */
export default function useRecordNameSearch(
  resourceType: string | undefined,
  text: string,
  scope: RecordSearchScope = "organization",
): RecordNameSearch {
  const query = text.trim();
  const tooLong = query.length > PERMISSION_TARGET_SEARCH_MAXIMUM_LENGTH;
  const asked = resourceType !== undefined && query.length > 0 && !tooLong && canSearchRecordsOf(resourceType, scope);
  const searchable = asked && scope === "organization";

  // Memoized on purpose: the arguments reach RTK Query, and a fresh array each render would be a
  // fresh request.
  const args = useMemo(
    () => (searchable && resourceType ? { resourceTypes: [resourceType], query, pageSize: NAME_SEARCH_LIMIT } : skipToken),
    [searchable, resourceType, query],
  );
  const answer = useSearchPermissionTargetsQuery(args);
  const readable = useReadableRecordNameSearch(asked && scope === "readable" ? resourceType : undefined, query);
  // This search's answer only: `data` keeps the previous text's records while this one is in flight.
  const current = searchable ? answer.currentData : undefined;
  const failed = searchable && (answer.isError || (current?.failed.length ?? 0) > 0);

  const ids = useMemo(() => current?.targets.map((target) => target.id), [current]);

  if (tooLong && resourceType !== undefined && canSearchRecordsOf(resourceType, scope))
    return { ids: undefined, isSearching: false, error: RECORD_SEARCH_TOO_LONG };
  if (scope === "readable") return readable;
  return {
    ids: failed ? undefined : ids,
    isSearching: searchable && answer.isFetching,
    error: failed ? RECORD_SEARCH_FAILED : undefined,
  };
}
